/**
 * Axios instance configuration with interceptors
 * Handles authentication, error handling, and request/response transformation
 */
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { ApiError } from '@core/types';

/**
 * Creates and configures the axios instance
 * @returns Configured axios instance
 */
export const createAxiosInstance = (): AxiosInstance => {
    const envURL = window._env_.FTDQADC_API_BASE_URL;
    let baseURL = 'http://localhost:8000/api/v1';
    
    // Use env URL if provided
    if (envURL) {
        baseURL = envURL;
        // Ensure baseURL always ends with /v1 (handle both /v1 and /v1/)
        const normalizedBaseURL = baseURL.replace(/\/+$/, ''); // Remove trailing slashes
        if (!normalizedBaseURL.endsWith('/v1')) {
            baseURL = normalizedBaseURL + '/v1';
        } else {
            baseURL = normalizedBaseURL;
        }
    }
    
    // Log baseURL in development to help debug
    if (import.meta.env.DEV) {
        console.log('🔧 Axios baseURL:', baseURL);
    }
    
    const instance = axios.create({
        baseURL,
        timeout: 30000,
        headers: {
            'Content-Type': 'application/json',
        },
    });

    // Request interceptor - Add auth token
    instance.interceptors.request.use(
        (config: InternalAxiosRequestConfig) => {
            const token = localStorage.getItem('accessToken');
            if (token && config.headers) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        },
        (error: AxiosError) => {
            return Promise.reject(error);
        }
    );

    // Response interceptor - Handle errors globally
    instance.interceptors.response.use(
        (response) => response,
        async (error: AxiosError<ApiError>) => {
            const originalRequest = error.config;

            // Handle 401 Unauthorized - Token refresh logic
            // Note: This is a fallback - the main refresh logic is in interceptors.ts
            // This will only run if the interceptor in interceptors.ts doesn't handle it
            if (error.response?.status === 401 && originalRequest) {
                // Skip if this is already a retry or if it's the refresh endpoint itself
                const requestConfig = originalRequest as InternalAxiosRequestConfig & { _retry?: boolean };
                if (requestConfig._retry || originalRequest.url?.includes('/auth/refresh')) {
                    return Promise.reject(error);
                }

                try {
                    const refreshToken = localStorage.getItem('refreshToken');
                    if (refreshToken) {
                        // Mark this request as a retry to prevent infinite loops
                        requestConfig._retry = true;

                        // Use the instance's baseURL instead of creating a new one
                        const baseURL = instance.defaults.baseURL || 'http://localhost:8000/api/v1';
                        const response = await axios.post(
                            `${baseURL}/auth/refresh`,
                            { refresh_token: refreshToken }
                        );

                        // Extract new access token (handle both snake_case and camelCase)
                        const newAccessToken = response.data?.access_token || response.data?.accessToken;
                        
                        if (newAccessToken) {
                            localStorage.setItem('accessToken', newAccessToken);

                            if (originalRequest.headers) {
                                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                            }

                            return instance(originalRequest);
                        } else {
                            throw new Error('No access token in refresh response');
                        }
                    }
                } catch (refreshError) {
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    window.location.href = '/login';
                    return Promise.reject(refreshError);
                }
            }

            return Promise.reject(error);
        }
    );

    return instance;
};

export const axiosInstance = createAxiosInstance();
